/**
 * Launcher Button
 *
 * What the on-page chat launcher puts on its face: the configured text, the
 * brand's header logo, or the default Chat glyph. Two independent tiers draw
 * that button — the React-free bootstrap (`entryPoints/bootstrap.ts`) and the
 * widget (`components/pages/BootstrapChatIcon.tsx`) — so the rules live here
 * rather than in either of them. Drift between the tiers shows as a flicker the
 * moment the widget chunk mounts over the bootstrap's button.
 *
 * Kept free of imports beyond the two domain helpers the bootstrap already
 * pulls in: this module lands in the always-loaded tier, which is gzip-budgeted
 * (see README.md#bundle-budgets).
 *
 * Reads `v2.appearance.launcherButton` only, never V1's top-level
 * `initialChatButton` — even though that field is required on every server config
 * and carries the same TEXT/ICON choice. Back-filling one from the other is the
 * porting side's job, not the widget's, and it is not done yet: `translateV1ToV2`
 * in agent-desktop reads `initialChatButton.value` only when the type is `ICON`
 * (mapping it to the header expand glyph) and never writes `launcherButton`, so a
 * V1 org with a TEXT launcher ports to an icon today. Closing that is sc-260489's
 * acceptance. Reading `initialChatButton` directly as a fallback here would be
 * wrong for Enhanced, where plenty of configs still carry a stale `type: 'TEXT'`
 * inherited from V1 that the admin never chose for the new experience.
 */

import { INITIAL_BUTTON_TYPES, LAUNCHER_DISPLAY } from '@/domain/configuration';
import { isLegacyUIEnabled, type LegacyUISource } from '@/domain/legacyUiConfig';

/**
 * Structural view of the config this reads. Optional at every level so a partial
 * or hand-authored config, and the full ServerConfiguration, both satisfy it.
 */
export type LauncherButtonSource = LegacyUISource & {
  v2?: {
    appearance?: {
      header?: { logo?: string };
      launcherButton?: { text?: string; type?: string; useLogo?: boolean };
    };
    display?: string;
  };
};

export interface ResolvedLauncherButton {
  /** The logo to draw, or undefined to fall through to the Chat glyph. */
  logoUrl: string | undefined;
  /** The label to draw, or undefined for a graphic (logo or glyph) button. */
  text: string | undefined;
}

/**
 * Resolve what the launcher should show.
 *
 * `isExistingUser` is the shopper's persisted identity — `user.id`, the same
 * predicate V1's `isExistingUserSelector` used.
 */
export function resolveLauncherButton(
  server: LauncherButtonSource | null | undefined,
  { isExistingUser }: { isExistingUser: boolean },
): ResolvedLauncherButton {
  const legacyUI = isLegacyUIEnabled(server);
  const appearance = server?.v2?.appearance;
  const launcherButton = appearance?.launcherButton;

  const text = resolveText({ isExistingUser, legacyUI, launcherButton });
  // A text button is a pill sized by its label with nothing else in it (V1's
  // `.initialChatButton`), so a logo has nowhere to go — `type` picks
  // text-vs-graphic, `useLogo` only picks which graphic.
  const logoUrl = text === undefined ? resolveLogo({ appearance, legacyUI }) : undefined;

  return { logoUrl, text };
}

/**
 * True when the configuration asks for the button instead of the chat bar on every
 * page. The Legacy UI has no bar, so it always asks for the button. A page rule and a
 * dismissed bar can also show the button; the callers check those separately.
 */
export function isButtonLauncherForced(server: LauncherButtonSource | null | undefined): boolean {
  return isLegacyUIEnabled(server) || server?.v2?.display === LAUNCHER_DISPLAY.BUTTON;
}

function resolveText({
  isExistingUser,
  legacyUI,
  launcherButton,
}: {
  isExistingUser: boolean;
  legacyUI: boolean;
  launcherButton: { text?: string; type?: string } | undefined;
}): string | undefined {
  if (launcherButton?.type !== INITIAL_BUTTON_TYPES.TEXT) return undefined;
  // V1 never showed the text button to a returning shopper, falling back to the
  // icon (web/'s MinimizedChatButton.jsx). Basic exists to reproduce V1, so it
  // inherits that; Enhanced has no V1 to be faithful to and simply obeys the
  // admin's choice.
  if (legacyUI && isExistingUser) return undefined;
  // An empty or whitespace-only label would render a blank pill, so it falls
  // back to the glyph rather than leaving the button with nothing on it.
  const text = launcherButton.text?.trim();
  return text ? text : undefined;
}

function resolveLogo({
  appearance,
  legacyUI,
}: {
  appearance: { header?: { logo?: string }; launcherButton?: { useLogo?: boolean } } | undefined;
  legacyUI: boolean;
}): string | undefined {
  const logo = appearance?.header?.logo;
  if (!logo) return undefined;
  // Absent is not `false`. Before this field existed, both tiers drew the header
  // logo on the Enhanced launcher whenever one was set and never on the legacy
  // one — so an org that has not answered the question yet keeps exactly that,
  // and no existing launcher changes on deploy. Admin writes an explicit
  // true/false from the day the control ships.
  const useLogo = appearance?.launcherButton?.useLogo ?? !legacyUI;
  return useLogo ? logo : undefined;
}
